import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import * as LogRocket from 'logrocket';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor() { }

  current_user: Object = {};
  csrf_token: string = "";
  logout_token: string = "";
  user_id: number;
  userAuthenticated: boolean = false;
  private _studios: any[] = [];
  private _selectedStudio: any = null;
  userAgreedToAgreement: boolean = false;

  // Subject to notify components when studios are updated
  studiosUpdated = new BehaviorSubject<any[]>([]);
  // Subject to notify components when the selected studio changes
  selectedStudioChanged = new BehaviorSubject<any>(null);

  /**
   * Helper method to always get the current selected studio ID.
   * This helps maintain compatibility with legacy code that uses studios[0].
   */
  getCurrentStudioId(): any {
    return this._selectedStudio?.id;
  }

  get studios(): any[] {
    return this._studios;
  }

  set studios(studios: any[]) {
    this._studios = studios;
    this.studiosUpdated.next(studios);

    // If there's no selected studio yet but we have studios, set the first one as selected
    if ((!this._selectedStudio || !this._selectedStudio.id) && studios && studios.length > 0) {
      this.selectedStudio = studios[0];
    }

    // Update LogRocket with studio information if it's initialized (skip for admin users)
    if (this.logRocketInitialized && this.current_user?.['uid'] != 1 && this.current_user?.['uid'] != 2658) {
      this.updateLogRocketStudios();
    }
  }

  get selectedStudio(): any {
    return this._selectedStudio;
  }

  set selectedStudio(studio: any) {
    this._selectedStudio = studio;
    this.selectedStudioChanged.next(studio);

    // Update API requests with this studio ID
    LoggerService.log('[STUDIO] Selected studio changed to:', studio?.key || 'None');

    // Update LogRocket with the new selected studio (skip for admin users)
    if (this.logRocketInitialized && this.current_user?.['uid'] != 1 && this.current_user?.['uid'] != 2658) {
      this.updateLogRocketStudios();
    }
  }

  is_user_authenticated() {
    return this.userAuthenticated;
  }

  set_user_authenticated(boolean: boolean) {
    this.userAuthenticated = boolean;

    if (!boolean) {
      // User logged out - reset LogRocket flag
      this.logRocketInitialized = false;
      if ((window as any).LogRocket) {
        LoggerService.log('[LOGROCKET] User logged out. Session tracking stopped.');
      }
      return;
    }

    // User is being authenticated - validate BEFORE any LogRocket initialization
    const uid = this.current_user?.['uid'];

    // Convert uid to number if it's a string (Drupal returns strings)
    const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : uid;

    // Check if uid exists and is a valid number
    if (!uidNum || typeof uidNum !== 'number' || isNaN(uidNum)) {
      LoggerService.log('[LOGROCKET] Will not initialize - Invalid uid type:', typeof uid, 'value:', uid);
      this.logRocketInitialized = false;
      return;
    }

    // Check if uid is 0 (anonymous user)
    if (uidNum === 0 || uidNum <= 0) {
      LoggerService.log('[LOGROCKET] Will not initialize - Anonymous user detected (uid:', uidNum, ')');
      this.logRocketInitialized = false;
      return;
    }

    // Check if uid is admin
    if (uidNum === 1 || uidNum === 2658) {
      LoggerService.log('[LOGROCKET] Will not initialize - Admin user detected (uid:', uidNum, ')');
      this.logRocketInitialized = false;
      return;
    }

    // Only NOW, after all validations pass, initialize LogRocket
    LoggerService.log('[LOGROCKET] Valid non-admin user detected (uid:', uidNum, '). Proceeding with initialization.');
    this.initializeAndIdentifyLogRocket();
  }

  private logRocketInitialized = false;

  // Initialize and identify user in LogRocket (combined method)
  private initializeAndIdentifyLogRocket(): void {
    // Double-check validation (defensive programming)
    const uid = this.current_user?.['uid'];

    // Convert uid to number if it's a string (Drupal returns strings)
    const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : uid;

    if (!uidNum || typeof uidNum !== 'number' || uidNum <= 0 || uidNum === 1 || uidNum === 2658) {
      LoggerService.log('[LOGROCKET] Initialization blocked - Invalid or admin user (uid:', uid, ')');
      this.logRocketInitialized = false;
      return;
    }

    // Skip if already initialized
    if (this.logRocketInitialized) {
      // Just update the user info if LogRocket is already initialized
      this.identifyUserInLogRocket();
      return;
    }

    try {
      // Initialize LogRocket
      LogRocket.init(environment.logRocketKey, {
        console: {
          isEnabled: true
        },
        network: {
          isEnabled: true
        },
        dom: {
          isEnabled: true
        },
        release: environment.name || (environment.production ? 'production' : 'local')
      });

      LoggerService.log('[LOGROCKET] Successfully initialized for user:', uidNum);

      // Make LogRocket globally available
      (window as any).LogRocket = LogRocket;

      // Register session URL callback
      LogRocket.getSessionURL((sessionURL) => {
        if (sessionURL) {
          LoggerService.log('[LOGROCKET] Session:', sessionURL);
          (window as any).logRocketSessionURL = sessionURL;
        }
      });

      this.logRocketInitialized = true;

      // Now identify the user
      this.identifyUserInLogRocket();
    } catch (error) {
      LoggerService.error('[LOGROCKET] Failed to initialize:', error);
      this.logRocketInitialized = false;
    }
  }

  // Identify the user in LogRocket
  private identifyUserInLogRocket(): void {
    // Validate user has a valid numeric uid > 0
    const uid = this.current_user?.['uid'];

    // Convert uid to number if it's a string (Drupal returns strings)
    const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : uid;

    if (!uidNum || typeof uidNum !== 'number' || uidNum <= 0) {
      LoggerService.log('[LOGROCKET] Invalid or anonymous user. Skipping identification.');
      return;
    }

    // Skip for admin users
    if (uidNum === 1 || uidNum === 2658) {
      LoggerService.log('[LOGROCKET] Admin user detected. Skipping identification.');
      return;
    }

    // Only identify authenticated users if LogRocket is initialized
    if (this.userAuthenticated && this.current_user?.['uid'] && this.logRocketInitialized) {
      try {
        // Get LogRocket from window
        const LogRocket = (window as any).LogRocket;

        if (!LogRocket) {
          LoggerService.warn('[LOGROCKET] LogRocket not available on window');
          return;
        }

        LoggerService.log('[LOGROCKET] Identifying user:', this.current_user['uid']);

        // Track authentication event
        LogRocket.track('User Authenticated', {
          uid: this.current_user['uid'],
          timestamp: new Date().toISOString()
        });

        // Get studio information from the studios array
        let studioInfo = 'None';
        let studioCount = 0;
        let selectedStudioInfo = 'None';

        if (this.studios && this.studios.length > 0) {
          studioInfo = this.studios.map(s => s.key || s.name || s.title || 'Unknown').join(', ');
          studioCount = this.studios.length;
        }

        if (this.selectedStudio) {
          selectedStudioInfo = this.selectedStudio.key || this.selectedStudio.name || 'None';
        }

        const userData = {
          name: this.current_user['name'],
          email: this.current_user['email'],
          studios: studioInfo,
          studioCount: studioCount,
          selectedStudio: selectedStudioInfo
        };

        LogRocket.identify(`${this.current_user['uid']}`, userData);
        LoggerService.log('[LOGROCKET] User identified:', this.current_user['uid']);
      } catch (error) {
        LoggerService.error('[LOGROCKET] Error identifying user:', error);
      }
    }
  }

  has_user_agreed_to_agreement() {
    return this.userAgreedToAgreement;
  }

  // Update LogRocket with current studio information
  async updateLogRocketStudios(): Promise<void> {
    // Strict validation before any LogRocket operations
    const uid = this.current_user?.['uid'];

    // Convert uid to number if it's a string (Drupal returns strings)
    const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : uid;

    if (!uidNum || typeof uidNum !== 'number' || uidNum <= 0 || uidNum === 1 || uidNum === 2658) {
      return;
    }

    // Only update if user is authenticated and LogRocket is available
    if (this.userAuthenticated && this.current_user?.['uid'] && this.logRocketInitialized) {
      try {
        const LogRocket = (window as any).LogRocket;

        if (!LogRocket) {
          return; // LogRocket not yet available
        }

        // Get studio information from the studios array
        let studioInfo = 'None';
        let studioCount = 0;
        let selectedStudioInfo = 'None';

        if (this.studios && this.studios.length > 0) {
          studioInfo = this.studios.map(s => s.key || s.name || s.title || 'Unknown').join(', ');
          studioCount = this.studios.length;
        }

        if (this.selectedStudio) {
          selectedStudioInfo = this.selectedStudio.key || this.selectedStudio.name || 'None';
        }

        // Update user attributes in LogRocket
        const userData = {
          name: this.current_user['name'],
          email: this.current_user['email'],
          studios: studioInfo,
          studioCount: studioCount,
          selectedStudio: selectedStudioInfo
        };

        LogRocket.identify(`${this.current_user['uid']}`, userData);
        LoggerService.log('[LOGROCKET] Updated studio context - Selected:', selectedStudioInfo, '| Total studios:', studioCount);
      } catch (error) {
        LoggerService.error('[LOGROCKET] Error updating studios:', error);
      }
    }
  }

  set_user_agreed_to_agreement(agreed: boolean) {
    this.userAgreedToAgreement = agreed;
  }
}
